/**
 * Hooks for generating Edge Function proxy URLs for private storage images.
 * Replaces the old signed URL approach - all image access now goes through
 * the get-storage-image Edge Function for consistent authorization.
 *
 * Supported buckets:
 * - checklist-images: Requires JWT + tenant membership + operative role
 * - tenant-logos: Requires JWT + tenant membership
 * - avatars: Requires JWT only
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

/**
 * Build a proxy URL for the get-storage-image Edge Function
 */
export function buildImageProxyUrl(path: string, bucket = "checklist-images"): string {
  // Strip cache buster if present to get clean path
  const cleanPath = path.split("?")[0];
  const encodedPath = encodeURIComponent(cleanPath);
  const encodedBucket = encodeURIComponent(bucket);
  return `${SUPABASE_URL}/functions/v1/get-storage-image?path=${encodedPath}&bucket=${encodedBucket}`;
}

/**
 * Extract storage path from legacy Supabase public URLs
 * E.g., "https://xxx.supabase.co/storage/v1/object/public/checklist-images/defects/123.jpg"
 * -> "defects/123.jpg"
 */
export function extractPathFromPublicUrl(url: string, bucket = "checklist-images"): string | null {
  const pattern = new RegExp(`/storage/v1/object/public/${bucket}/(.+)$`);
  const match = url.match(pattern);
  return match ? match[1] : null;
}

/**
 * Check if a path/URL is a legacy public URL that needs conversion
 */
function isLegacyPublicUrl(pathOrUrl: string): boolean {
  return pathOrUrl.includes("/storage/v1/object/public/");
}

/**
 * Extract storage path from any URL format or return direct path
 */
export function extractStoragePath(pathOrUrl: string, bucket = "checklist-images"): string {
  // Data URLs should not be processed
  if (pathOrUrl.startsWith("data:")) {
    return pathOrUrl;
  }

  // Strip cache buster query params
  const withoutCacheBuster = pathOrUrl.split("?")[0];

  // Already a relative path (most common case for new data)
  if (!withoutCacheBuster.startsWith("http://") && !withoutCacheBuster.startsWith("https://")) {
    return withoutCacheBuster;
  }

  // Try to extract from legacy public URL
  const extracted = extractPathFromPublicUrl(withoutCacheBuster, bucket);
  if (extracted) {
    return extracted;
  }

  // Try to extract from Supabase storage URL pattern
  try {
    const url = new URL(withoutCacheBuster);
    const match = url.pathname.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)/);
    if (match && match[1] === bucket) {
      return decodeURIComponent(match[2]);
    }
  } catch {
    // Invalid URL, return as-is
  }

  // Return original if we can't extract
  return withoutCacheBuster;
}

/**
 * Hook to get a single proxy URL for an image path
 * Synchronous - no API call needed
 */
export function useImageProxyUrl(
  pathOrUrl: string | null | undefined,
  bucket = "checklist-images",
): string | null {
  if (!pathOrUrl) return null;

  // Data URLs are returned directly
  if (pathOrUrl.startsWith("data:")) return pathOrUrl;

  const path = extractStoragePath(pathOrUrl, bucket);
  return buildImageProxyUrl(path, bucket);
}

/**
 * Hook to get multiple proxy URLs for image paths
 * Synchronous - no API call needed
 */
export function useImageProxyUrls(
  pathsOrUrls: (string | null | undefined)[],
  bucket = "checklist-images",
): (string | null)[] {
  return pathsOrUrls.map((p) => {
    if (!p) return null;
    if (p.startsWith("data:")) return p;
    const path = extractStoragePath(p, bucket);
    return buildImageProxyUrl(path, bucket);
  });
}

/**
 * Hook to get proxy URL for avatar images
 * Handles both legacy public URLs and new relative paths
 */
export function useAvatarProxyUrl(pathOrUrl: string | null | undefined): string | null {
  if (!pathOrUrl) return null;
  if (pathOrUrl.startsWith("data:")) return pathOrUrl;

  const path = extractStoragePath(pathOrUrl, "avatars");
  return buildImageProxyUrl(path, "avatars");
}

/**
 * Hook to get proxy URL for tenant logo images
 * Handles both legacy public URLs and new relative paths
 */
export function useTenantLogoProxyUrl(pathOrUrl: string | null | undefined): string | null {
  if (!pathOrUrl) return null;
  if (pathOrUrl.startsWith("data:")) return pathOrUrl;

  const path = extractStoragePath(pathOrUrl, "tenant-logos");
  return buildImageProxyUrl(path, "tenant-logos");
}
